/* eslint-disable @atlaskit/platform/no-preconditioning -- Existing chat-store actions gate feature behavior from runtime flags. */

import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { type ConversationChannelsResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	isTerminalTaskState,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { mapApiTaskStatusToProgressState } from '@atlassian/conversation-assistant-store/controllers/tasks-shared/task-helpers';

import { StoreErrorFlagId } from '../../common/ui/hooks/use-show-store-error-flag/StoreErrorFlagId';
import { CONVERSATION_LIST_PAGE_SIZE } from './constants';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { getProcessedConversationChannels } from './getProcessedConversationChannels';
import { isEmptyConversation } from './isEmptyConversation';
import { loadMoreConversationChannelsData } from './loadMoreConversationChannelsData';
import { mergeConversationChannelTasks } from './mergeConversationChannelTasks';
import { restoreTaskModeIfNeeded } from './restoreTaskModeIfNeeded';
import { setSelectedConversation } from './setSelectedConversation';
import type { ChatStoreAction, FetchConversationChannelsDataArgs } from './types';

export const fetchConversationChannelsData: SafeAction<[FetchConversationChannelsDataArgs?]> =
	createSafeAction(
		StoreActionLoggableName.fetchConversationChannelsData,
		({
			isInitialLoad = false,
			reconcileFirstPage = false,
		}: FetchConversationChannelsDataArgs = {}): ChatStoreAction =>
			async (
				{ setState, getState, dispatch },
				{ assistanceService, experienceTracker, unifiedTasksActions },
			) => {
				if (
					reconcileFirstPage &&
					!getState().conversationPagination.isConversationListMounted &&
					(!(
						fg('rovo_chat_suggestions_empty_states') &&
						UNSAFE_expValNoExposure(
							'rovo_chat_schedule_chat_skill_experiment',
							'isEnabled',
							false,
						) === true
					) ||
						!getState().isNeedsReviewListMounted)
				) {
					return;
				}

				// A concurrent load-more may be in flight; skip the first-page reconciliation
				// rather than stomping its cursor/hasMore metadata. Best-effort: the next
				// visibility event will reconcile correctly.
				if (reconcileFirstPage && getState().conversationPagination.isLoadingMore) {
					return;
				}

				if (isInitialLoad) {
					experienceTracker.start({
						id: ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD,
						name: ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD,
					});
				} else {
					experienceTracker.start({
						id: ExperienceName.GET_CONVERSATION_CHANNELS,
						name: ExperienceName.GET_CONVERSATION_CHANNELS,
					});
				}

				// Capture conversation IDs before API call to detect changes during the call
				const conversationsBeforeAPICall = new Set(Object.keys(getState().conversations));

				let conversationChannelsResponse: ConversationChannelsResponse;
				try {
					conversationChannelsResponse = await assistanceService.getConversationChannels({
						limit: CONVERSATION_LIST_PAGE_SIZE,
					});
				} catch (error) {
					if (isInitialLoad && fg('rovo_chat_non_blocking_conversation_history_failure')) {
						setState({
							isConversationsInitialized: true,
							conversationPagination: {
								...getState().conversationPagination,
								isLoadingMore: false,
								loadMoreError: null,
							},
						});
					}
					throw error;
				}

				const channelsFromAPI = conversationChannelsResponse.items;
				const { conversations, currentConversationId } = getState();

				const channelIds = new Set(channelsFromAPI.map((channel) => channel.id));

				// Identify conversations deleted during the API call (were there before but aren't now)
				const conversationsDeletedDuringCall = new Set(
					Array.from(conversationsBeforeAPICall).filter(
						(id) => !conversations[id] && !isEmptyConversation(id),
					),
				);

				// Identify conversations created during the API call (exist in state but weren't there before and aren't in API response)
				const conversationsCreatedDuringCall = new Set(
					Object.keys(conversations).filter(
						(id) => !conversationsBeforeAPICall.has(id) && !channelIds.has(id),
					),
				);

				// Preserve all local conversations not yet on the server
				// (they may have just finished processing or be in-flight)
				// e.g. optimistic conversation when URL params are used to open a chat
				const optimisticConversationsToPreserve = new Set(
					Object.keys(conversations).filter(
						(id) => !channelIds.has(id) && !isEmptyConversation(id),
					),
				);

				const conversationIdsToPreserve = new Set([
					...conversationsCreatedDuringCall,
					...optimisticConversationsToPreserve,
				]);

				const conversationsToPreserve = Object.fromEntries(
					Array.from(conversationIdsToPreserve, (id) => [id, conversations[id]]),
				);

				const processedChannels = getProcessedConversationChannels({
					channelsFromAPI,
					conversations,
					conversationsDeletedDuringCall,
				});

				mergeConversationChannelTasks(channelsFromAPI, unifiedTasksActions);

				// Hydrate the store with the fetched conversation channels
				// whilst preserving the empty state and and any conversations created during the API call, so drafts are never lost
				// due to rehydration (e.g window refocus) when this is called
				setState({
					conversations: {
						EMPTY_CONVERSATION_ID: conversations[EMPTY_CONVERSATION_ID],
						...processedChannels,
						...conversationsToPreserve,
					},
					isConversationsInitialized: true,
					conversationPagination: {
						...getState().conversationPagination,
						nextCursor: conversationChannelsResponse.cursor,
						hasMore: conversationChannelsResponse.hasNext,
						isLoadingMore: false,
						loadMoreError: null,
					},
				});

				if (
					isInitialLoad &&
					fg('rovo_chat_conversation_search_pagination') &&
					getState().conversationPagination.isConversationListMounted
				) {
					void dispatch(loadMoreConversationChannelsData());
				}

				const currentConversationDeleted =
					!isEmptyConversation(currentConversationId) &&
					!channelIds.has(currentConversationId) &&
					!conversationIdsToPreserve.has(currentConversationId);

				if (currentConversationDeleted && !isInitialLoad) {
					dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));
				}

				const selectedConversationTasks =
					channelsFromAPI.find((channel) => channel.id === currentConversationId)?.tasks ?? [];
				const shouldRestoreTaskMode = selectedConversationTasks.some(
					(task) => !isTerminalTaskState(mapApiTaskStatusToProgressState(task.status)),
				);

				if (isInitialLoad && fg('rovo_chat_enable_tasks') && shouldRestoreTaskMode) {
					dispatch(restoreTaskModeIfNeeded());
				}

				experienceTracker.succeed({
					name: isInitialLoad
						? ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD
						: ExperienceName.GET_CONVERSATION_CHANNELS,
				});
			},
		{
			experienceName: (args) =>
				args?.[0]?.isInitialLoad
					? ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD
					: ExperienceName.GET_CONVERSATION_CHANNELS,
			actionErrorHandler: (context) => {
				const { onError, ...args } = context.args?.[0] ?? {};
				if (args.isInitialLoad) {
					if (fg('rovo_chat_non_blocking_conversation_history_failure')) {
						context.containerProps.showFlag?.(StoreErrorFlagId.CONVERSATION_HISTORY_LOAD_FAILED);
						return undefined;
					}
					return { proceedToDefaultHandling: true };
				}
				return onError ? onError({ ...context, args }) : { proceedToDefaultHandling: true };
			},
		},
	);
